/**
 * Monday–Sunday week windows for the "Due …" quick-filter tabs.
 *
 * Shared so the Schedule Lines and PO Lines surfaces cut the week the same
 * way. They were written independently first; if the two ever disagreed on
 * where a week starts, the same delivery would count on one tab and not the
 * other, and the discrepancy would look like missing data rather than a
 * calendar difference.
 *
 * Weeks are Monday-based and computed in the browser's local timezone,
 * matching how a planner reads "this week". Dates are formatted as plain
 * `YYYY-MM-DD` rather than ISO timestamps — the API filters on date columns,
 * and `toISOString()` would shift the boundary by the UTC offset (a Monday
 * 00:00 local becomes the previous Sunday in UTC west of Greenwich).
 */

/** Format a Date as local `YYYY-MM-DD`. */
function fmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Monday 00:00 of the week containing `today`. */
function mondayOfThisWeek(today: Date = new Date()): Date {
  const daysSinceMonday = (today.getDay() + 6) % 7; // Sunday(0) -> 6
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysSinceMonday);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

/**
 * This week, Monday to Sunday inclusive.
 * @returns `{ from, to }` as `YYYY-MM-DD`
 */
export function getThisWeekRange(today: Date = new Date()): { from: string; to: string } {
  const monday = mondayOfThisWeek(today);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: fmt(monday), to: fmt(sunday) };
}

/**
 * Next week, Monday to Sunday inclusive.
 * @returns `{ from, to }` as `YYYY-MM-DD`
 */
export function getNextWeekRange(today: Date = new Date()): { from: string; to: string } {
  const nextMonday = mondayOfThisWeek(today);
  nextMonday.setDate(nextMonday.getDate() + 7);
  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);
  return { from: fmt(nextMonday), to: fmt(nextSunday) };
}

/**
 * The Sunday immediately before this week — the upper bound for arrears.
 *
 * "Due From Previous Weeks" is everything still open on or before this date,
 * kept as its own window so "Due This Week" stays a clean planner view rather
 * than accumulating every overdue line.
 *
 * @returns `YYYY-MM-DD`
 */
export function getLastSundayBeforeThisWeek(today: Date = new Date()): string {
  const lastSunday = mondayOfThisWeek(today);
  lastSunday.setDate(lastSunday.getDate() - 1);
  return fmt(lastSunday);
}
